import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Form,
  InputGroup,
  OverlayTrigger,
  Popover,
  ToggleButton,
} from "react-bootstrap";
import MyPagination from "../Common/MyPagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SingleContactCard from "../Contacts/SingleContactCard";
import {
  faEnvelope,
  faGear,
  faMessage,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import MySearch from "../Common/MySearch";
import {
  ImportedContactFileInfoType,
  ImportedRecordType,
  UpdateRecordPopover,
} from "./ImportContactsModal";
import {
  SaveImportedContactsOptionsType,
  useGetContactFieldsQuery,
  useGetFieldsForImportedFileQuery,
  useLazyGetTempRecordsQuery,
  useDeleteTempRecordsMutation,
  useSaveImportedContactsMutation,
} from "../store/slices/contactsSlice";
import MyModal from "../Common/MyModal";
import ContactFieldEditor from "../Contacts/ContactFieldEditor";
import { toast } from "react-toastify";
import JsonCollapse from "../Builder/Components/JsonCollapse";

const normalizeFieldName = (field: string) =>
  (field || "")
    .toLowerCase()
    .trim()
    .replace(/[\s_-]/g, "");

const SYSTEM_FIELDS = new Set(
  ["status", "groups", "assets", "latitude", "longitude"].map(
    normalizeFieldName
  )
);

export function ImportFileAsContacts({
  importedFileId,
}: {
  importedFileId: string;
}) {
  const [fileInfo, setFileInfo] = useState<ImportedContactFileInfoType>();
  const [recordsData, setRecordsData] = useState<ImportedRecordType[]>();
  const [total, setTotal] = useState(0);
  const [channels, setChannels] = useState(["sms", "email", "voice"]);
  const [withContacts, setWithContacts] = useState(false);
  const [withDuplicatedRows, setWithDuplicatedRows] = useState(false);
  const [withDuplicatedContacts, setWithDuplicatedContacts] = useState(false);
  const [duplicatedIndex, setDuplicatedIndex] = useState<number | undefined>(
    undefined
  );
  const [search, setSearch] = useState("");
  const [selectedRecords, setSelectedRecords] = useState<number[]>([]);
  const [forceRefresh, setForceRefresh] = useState<string>(
    new Date().toLocaleString()
  );

  const [createNewFields, setCreateNewFields] = useState<{
    [key: string]: any;
  }>({});

  const [saveContactsOptions, setSaveContactsOptions] =
    useState<SaveImportedContactsOptionsType>({
      fileUuid: "",
      rowStart: 0,
      rowEnd: 0,
      matched: true,
      notMatched: true,
      validOnly: true,
    });
  const contactFieldsData = useGetContactFieldsQuery();
  const fieldsData = useGetFieldsForImportedFileQuery(fileInfo?.uuid || "", {
    skip: !fileInfo?.uuid,
  });
  const [saveImportedContacts, { isLoading: isSavingContacts }] =
    useSaveImportedContactsMutation();
  const [fetchRecords] = useLazyGetTempRecordsQuery();
  const [deleteTempRecords] = useDeleteTempRecordsMutation();

  const loadRecords = async (page = 0, perPage = 12) => {
    try {
      const records = await fetchRecords({
        file: importedFileId,
        channels,
        withContacts,
        withDuplicatedRows,
        withDuplicatedContacts,
        duplicatedIndex,
        search: search !== "" ? search : undefined,
        page,
        perPage,
      }).unwrap();
      setFileInfo(records.file);
      setRecordsData(records.data);
      setTotal(records.total);
      setSaveContactsOptions((prev) => ({
        ...prev,
        fileUuid: records.file.uuid || "",
        rowStart: 0,
        rowEnd: records.file.stats.rows,
      }));
    } catch {
      setTimeout(() => {
        loadRecords(page, perPage);
      }, 1000);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await deleteTempRecords({
        file: importedFileId,
        rowIndexes: selectedRecords,
      }).unwrap();
      setForceRefresh(new Date().toLocaleString());
      setSelectedRecords([]);
    } catch {
      toast.error("Failed to delete records.");
    }
  };

  const handleDeleteFiltered = async () => {
    try {
      await deleteTempRecords({
        file: importedFileId,
        withContacts,
        withDuplicatedRows,
        withDuplicatedContacts,
      }).unwrap();
      setForceRefresh(new Date().toLocaleString());
    } catch {
      toast.error("Failed to delete records.");
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadRecords();
    }, 300);
    return () => clearTimeout(timeout);
  }, [
    channels,
    withContacts,
    withDuplicatedRows,
    withDuplicatedContacts,
    duplicatedIndex,
    search,
    forceRefresh,
  ]);

  const columns = fileInfo?.header || [];

  const handleSaveContacts = async () => {
    if (!saveContactsOptions.fileUuid) {
      toast.error("Imported file uuid is missing.");
      return;
    }

    try {
      await saveImportedContacts({
        createNewFields: Object.values(createNewFields),
        ...saveContactsOptions,
      }).unwrap();
      toast.success("Contacts saved successfully.");
      fieldsData.refetch();
    } catch {
      toast.error("Failed to save contacts.");
    }
  };

  return (
    fileInfo && (
      <>
        <h5 className="mt-2 mb-3">
          import contacts from file: {fileInfo.file}
        </h5>
        <div className="mb-2">
          <MySearch
            value={search}
            onChange={(e: any) => setSearch(e.target.value)}
            placeholder="Search for a contact"
          />
        </div>
        <div className="d-flex align-items-center mb-2">
          <div className="d-flex align-items-center me-3">
            <div className="me-1">Channels:</div>
            <Input
              value={channels}
              onChange={setChannels}
              isMulti
              inputType="select"
              options={["sms", "email", "voice"].map((c) => ({
                label: c,
                value: c,
              }))}
            />
          </div>
          <Form.Check
            id="with-contacts-checkbox"
            type="checkbox"
            label="With contacts"
            checked={withContacts}
            onChange={(e) => setWithContacts(e.target.checked)}
            className="me-3"
          />
          <Form.Check
            id="with-duplicated-rows-checkbox"
            type="checkbox"
            label="With duplicated rows"
            checked={withDuplicatedRows}
            onChange={(e) => setWithDuplicatedRows(e.target.checked)}
            className="me-3"
          />

          <Form.Check
            id="with-duplicated-contacts-checkbox"
            type="checkbox"
            label="With duplicated contacts"
            checked={withDuplicatedContacts}
            onChange={(e) => setWithDuplicatedContacts(e.target.checked)}
            className="me-3"
          />
          <div>
            {selectedRecords.length > 0 && (
              <button
                className="btn btn-secondary ms-2 px-1 py-0"
                onClick={() => {
                  handleDeleteSelected();
                }}
              >
                Delete selected
              </button>
            )}
            {(withContacts || withDuplicatedContacts || withDuplicatedRows) && (
              <button
                className="btn btn-secondary ms-2 px-1 py-0"
                onClick={() => {
                  handleDeleteFiltered();
                }}
              >
                Delete filtered
              </button>
            )}
          </div>
        </div>

        {duplicatedIndex !== undefined && (
          <div className="alert alert-warning">
            Showing duplicated records for row index: {duplicatedIndex}.{" "}
            <button
              className="btn btn-link p-0 align-baseline ms-2"
              onClick={() => setDuplicatedIndex(undefined)}
            >
              Clear
            </button>
          </div>
        )}

        <div className="alert alert-secondary">
          These fields from the imported file were used to match with any
          existing contact field. The fields that exist in the system will be
          used to import new or update existing contacts. You can choose to
          create new fields that do not exist in the system or ignore any of the
          existing fields when importing or updating contacts.
        </div>

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Channels</th>
                {columns.map((c: any, idx: React.Key | null | undefined) => (
                  <th key={c}>
                    <div className="text-nowrap">
                      {c}
                      {normalizeFieldName(c) === "longitude" &&
                      columns.some(
                        (col: string) => normalizeFieldName(col) === "latitude"
                      ) ? null : (
                        <MyModal
                          label={
                            <span className="ms-2" title={"Settings"}>
                              <FontAwesomeIcon icon={faGear} />
                            </span>
                          }
                          heading={"Field settings"}
                          size="lg"
                        >
                          {!!fieldsData?.data?.fields?.[c]?.uuid ? (
                            <Form.Check
                              id={`ignore-field-${c}-checkbox`}
                              type="switch"
                              label={
                                <span>
                                  Ignore this field when importing contacts?
                                </span>
                              }
                              className="my-3"
                              checked={
                                saveContactsOptions.ignoreFieldUuids?.includes(
                                  fieldsData?.data?.fields?.[c]?.uuid || ""
                                ) || false
                              }
                              onChange={(e: any) => {
                                const checked = e.target.checked;
                                setSaveContactsOptions((prev) => {
                                  const newIgnoreFieldUuids = new Set(
                                    prev.ignoreFieldUuids || []
                                  );
                                  if (checked) {
                                    newIgnoreFieldUuids.add(
                                      fieldsData?.data?.fields?.[c]?.uuid || ""
                                    );
                                  } else {
                                    newIgnoreFieldUuids.delete(
                                      fieldsData?.data?.fields?.[c]?.uuid || ""
                                    );
                                  }
                                  return {
                                    ...prev,
                                    ignoreFieldUuids:
                                      Array.from(newIgnoreFieldUuids),
                                  };
                                });
                              }}
                            />
                          ) : SYSTEM_FIELDS.has(normalizeFieldName(c)) ? (
                            <Form.Check
                              id={`ignore-field-${c}-checkbox`}
                              type="switch"
                              label={
                                <span>
                                  Ignore this field when importing contacts?
                                </span>
                              }
                              className="my-3"
                              checked={
                                saveContactsOptions.ignoreFieldUuids?.includes(
                                  normalizeFieldName(c) === "latitude"
                                    ? "location"
                                    : normalizeFieldName(c)
                                ) || false
                              }
                              onChange={(e: any) => {
                                const checked = e.target.checked;
                                setSaveContactsOptions((prev) => {
                                  const newIgnoreFieldUuids = new Set(
                                    prev.ignoreFieldUuids || []
                                  );
                                  if (checked) {
                                    newIgnoreFieldUuids.add(
                                      normalizeFieldName(c) === "latitude"
                                        ? "location"
                                        : normalizeFieldName(c)
                                    );
                                  } else {
                                    newIgnoreFieldUuids.delete(
                                      normalizeFieldName(c) === "latitude"
                                        ? "location"
                                        : normalizeFieldName(c)
                                    );
                                  }
                                  return {
                                    ...prev,
                                    ignoreFieldUuids:
                                      Array.from(newIgnoreFieldUuids),
                                  };
                                });
                              }}
                            />
                          ) : (
                            <>
                              <div>
                                This field from the imported file was not
                                matched with any existing contact field. You can
                                choose to create it as a custom field.
                              </div>
                              <Form.Check
                                id={`create-new-field-${c}-checkbox`}
                                type="switch"
                                label={
                                  <span>
                                    Create new contact field for{" "}
                                    <span className="fw-bold">{c}</span>?
                                  </span>
                                }
                                className="my-3"
                                checked={c in createNewFields}
                                onChange={(e: any) => {
                                  const checked = e.target.checked;
                                  setCreateNewFields((prev) => {
                                    const newState = { ...prev };
                                    if (checked) {
                                      newState[c] = { label: c, isNew: true };
                                    } else {
                                      delete newState[c];
                                    }
                                    return newState;
                                  });
                                }}
                              />
                              {c in createNewFields && (
                                <ContactFieldEditor
                                  labelIsNotEditable
                                  value={
                                    createNewFields[c] || {
                                      label: c,
                                      isNew: true,
                                    }
                                  }
                                  onChange={(value: any) =>
                                    setCreateNewFields((prev) => ({
                                      ...prev,
                                      [c]: value,
                                    }))
                                  }
                                />
                              )}
                            </>
                          )}

                          {fieldsData?.data?.fields?.[c]?.isSelect &&
                            !!fieldsData?.data?.fields?.[c]?.options
                              ?.length && (
                              <Form.Check
                                id={`do-not-create-new-options-for-field-${c}-checkbox`}
                                type="switch"
                                label={
                                  <span>
                                    Do not create new options for this field
                                    when importing contacts?
                                  </span>
                                }
                                className="my-3"
                                disabled={saveContactsOptions.ignoreFieldUuids?.includes(
                                  fieldsData?.data?.fields?.[c]?.uuid || ""
                                )}
                                checked={
                                  saveContactsOptions.doNotCreateNewOptions?.includes(
                                    fieldsData?.data?.fields?.[c]?.uuid || ""
                                  ) || false
                                }
                                onChange={(e: any) => {
                                  const checked = e.target.checked;
                                  setSaveContactsOptions((prev) => {
                                    const newDoNotCreateNewOptions = new Set(
                                      prev.doNotCreateNewOptions || []
                                    );
                                    if (checked) {
                                      newDoNotCreateNewOptions.add(
                                        fieldsData?.data?.fields?.[c]?.uuid ||
                                          ""
                                      );
                                    } else {
                                      newDoNotCreateNewOptions.delete(
                                        fieldsData?.data?.fields?.[c]?.uuid ||
                                          ""
                                      );
                                    }
                                    return {
                                      ...prev,
                                      doNotCreateNewOptions: Array.from(
                                        newDoNotCreateNewOptions
                                      ),
                                    };
                                  });
                                }}
                              />
                            )}

                          {createNewFields[c]?.isSelect && (
                            <div className="alert alert-secondary">
                              This is a new select field and options will be
                              created based on the data from the imported file.
                              Here you can create additional options.
                            </div>
                          )}
                        </MyModal>
                      )}
                    </div>
                    {SYSTEM_FIELDS.has(normalizeFieldName(c)) ? (
                      saveContactsOptions.ignoreFieldUuids?.includes(
                        normalizeFieldName(c) === "latitude"
                          ? "location"
                          : normalizeFieldName(c)
                      ) ? (
                        <span
                          className="badge bg-secondary"
                          title="This field will be ignored when importing contacts."
                        >
                          field ignored
                        </span>
                      ) : (
                        <span
                          className="badge bg-success"
                          title="Field matched with existing contact field."
                        >
                          field exists
                        </span>
                      )
                    ) : fieldsData?.data?.fields?.[c]?.uuid ? (
                      saveContactsOptions.ignoreFieldUuids?.includes(
                        fieldsData?.data?.fields?.[c]?.uuid || ""
                      ) ? (
                        <span
                          className="badge bg-secondary"
                          title="This field will be ignored when importing contacts."
                        >
                          field ignored
                        </span>
                      ) : (
                        <span
                          className="badge bg-success"
                          title="Field matched with existing contact field."
                        >
                          field exists
                        </span>
                      )
                    ) : c in createNewFields ? (
                      <span
                        className="badge bg-success"
                        title="Field will be created."
                      >
                        field will be created
                      </span>
                    ) : (
                      <span
                        className="badge bg-warning"
                        title="Field not found."
                      >
                        field not found
                      </span>
                    )}
                  </th>
                ))}

                <th className="text-end"></th>
              </tr>
            </thead>
            <tbody>
              {recordsData?.map((row) => (
                <tr
                  key={row.rowIndex || 0}
                  className={row.contactsMatched > 1 ? "table-warning" : ""}
                >
                  <td>
                    <Form.Check
                      id={`record-${row.rowIndex || 0}-checkbox`}
                      type="checkbox"
                      checked={selectedRecords.includes(row.rowIndex || 0)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRecords([
                            ...selectedRecords,
                            row.rowIndex || 0,
                          ]);
                        } else {
                          setSelectedRecords(
                            selectedRecords.filter(
                              (r) => r !== (row.rowIndex || 0)
                            )
                          );
                        }
                      }}
                    />
                  </td>
                  <td className="p-2 text-nowrap">
                    {
                      // channel icons
                      [
                        { label: "isVoice", icon: faPhone },
                        { label: "isSms", icon: faMessage },
                        { label: "isEmail", icon: faEnvelope },
                      ].map((channel) => (
                        <FontAwesomeIcon
                          key={channel.label}
                          icon={channel.icon}
                          className={`p-0 text-purple me-3 ${
                            (row as any)[channel.label] ? "" : "opacity-25"
                          } `}
                        />
                      ))
                    }
                  </td>

                  {columns.map(
                    (field: any, idx: React.Key | null | undefined) => (
                      <td key={idx}>
                        <span>{row.data[field]}</span>

                        <UpdateRecordPopover
                          key={row.data[field]}
                          importedFileId={importedFileId}
                          row={row}
                          field={field}
                          setForceRefresh={setForceRefresh}
                        />
                      </td>
                    )
                  )}
                  <td className="text-end text-nowrap">
                    {row.rowDuplicates ? (
                      <OverlayTrigger
                        key={
                          "error-popover-" +
                          "rowDuplicates" +
                          "-" +
                          (row.rowIndex || 0) +
                          "-" +
                          duplicatedIndex
                        }
                        trigger="click"
                        placement="auto"
                        rootClose
                        overlay={
                          <Popover
                            id={
                              "error-popover-contacts-matched" +
                              (row.rowIndex || 0)
                            }
                          >
                            <Popover.Header as="h3">
                              Duplicated contact
                            </Popover.Header>
                            <Popover.Body>
                              The primary field
                              {fileInfo.matchBy.split(",").length > 1
                                ? "s"
                                : ""}{" "}
                              in this row{" "}
                              {fileInfo.matchBy.split(",").length > 1
                                ? "were"
                                : "was"}{" "}
                              duplicated by{" "}
                              <span className="fw-bold">
                                {row.rowDuplicates}
                              </span>{" "}
                              other row{row.rowDuplicates > 1 ? "s" : ""} in the
                              file.
                              {!!row.duplicatedRows?.length && (
                                <div>
                                  {row.rowDuplicates > 1
                                    ? "These rows have "
                                    : "This row has "}
                                  been removed:{" "}
                                  {row.duplicatedRows
                                    ?.map((dr) => parseInt(dr) + 1)
                                    ?.join(", ")}
                                </div>
                              )}
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <Button variant="btn btn-sm btn-secondary px-1 py-0 me-2 text-nowrap">
                          {row.rowDuplicates} duplicates
                        </Button>
                      </OverlayTrigger>
                    ) : null}

                    {row.contactsMatched > 1 ? (
                      <OverlayTrigger
                        key={
                          "error-popover-" +
                          "contactsMatched" +
                          "-" +
                          (row.rowIndex || 0)
                        }
                        trigger="click"
                        placement="auto"
                        rootClose
                        overlay={
                          <Popover
                            id={
                              "error-popover-contacts-matched" +
                              (row.rowIndex || 0)
                            }
                          >
                            <Popover.Header as="h3">
                              Matched contact
                            </Popover.Header>
                            <Popover.Body>
                              This row was matched by {row.contactsMatched}{" "}
                              contacts in the system and will not be imported.
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <Button variant="btn btn-sm btn-warning px-1 py-0 me-2 text-nowrap">
                          {row.contactsMatched} matches
                        </Button>
                      </OverlayTrigger>
                    ) : null}

                    {row.isContact ? (
                      <SingleContactCard uuid={row.contactUuid} />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <MyPagination
          total={total}
          onPaginate={loadRecords}
          forceRefresh={forceRefresh}
        />

        <div className="d-flex mb-3">
          <InputGroup>
            <InputGroup.Text>Import rows from</InputGroup.Text>
            <Form.Control
              type="number"
              value={saveContactsOptions.rowStart}
              onChange={(e) =>
                setSaveContactsOptions((prev) => ({
                  ...prev,
                  rowStart: Number(e.target.value),
                }))
              }
              min={0}
              max={fileInfo.stats.rows}
            />
            <InputGroup.Text>to</InputGroup.Text>
            <Form.Control
              type="number"
              value={saveContactsOptions.rowEnd}
              onChange={(e) =>
                setSaveContactsOptions((prev) => ({
                  ...prev,
                  rowEnd: Number(e.target.value),
                }))
              }
              min={0}
              max={fileInfo.stats.rows}
            />
          </InputGroup>
          <ButtonGroup className="ms-3 flex-grow-1 bg-light text-nowrap">
            <ToggleButton
              id={`radio-matched`}
              value={"matched"}
              type="radio"
              variant={
                saveContactsOptions.matched && !saveContactsOptions.notMatched
                  ? "secondary"
                  : "outline-secondary"
              }
              name="match-not-match"
              checked={
                saveContactsOptions.matched && !saveContactsOptions.notMatched
              }
              onChange={(e) => {
                const checked = e.target.checked;
                setSaveContactsOptions((prev) => ({
                  ...prev,
                  matched: checked,
                  notMatched: !checked,
                }));
              }}
            >
              Update existing
            </ToggleButton>
            <ToggleButton
              id={`create-new-only`}
              value={"create-new-only"}
              type="radio"
              variant={
                !saveContactsOptions.matched && saveContactsOptions.notMatched
                  ? "secondary"
                  : "outline-secondary"
              }
              name="match-not-match"
              checked={
                !saveContactsOptions.matched && saveContactsOptions.notMatched
              }
              onChange={(e) => {
                const checked = e.target.checked;
                setSaveContactsOptions((prev) => ({
                  ...prev,
                  matched: !checked,
                  notMatched: checked,
                }));
              }}
            >
              Create new contacts only
            </ToggleButton>
            <ToggleButton
              id={`radio-update-all`}
              value={"update-all"}
              type="radio"
              variant={
                saveContactsOptions.matched && saveContactsOptions.notMatched
                  ? "secondary"
                  : "outline-secondary"
              }
              name="match-not-match"
              checked={
                saveContactsOptions.matched && saveContactsOptions.notMatched
              }
              onChange={(e) => {
                const checked = e.target.checked;
                setSaveContactsOptions((prev) => ({
                  ...prev,
                  matched: checked,
                  notMatched: checked,
                }));
              }}
            >
              Update all contacts
            </ToggleButton>
          </ButtonGroup>
        </div>

        <div className="mb-2 d-flex justify-content-end">
          <button
            className="btn btn-colored rounded-pill"
            onClick={handleSaveContacts}
            disabled={!saveContactsOptions.fileUuid || isSavingContacts}
          >
            {isSavingContacts ? "Saving..." : "Import contacts"}
          </button>
        </div>
        <JsonCollapse json={saveContactsOptions} />
      </>
    )
  );
}
